<script lang="ts">
/**
 * Optional capabilities of the preview. Consumers opt in to the ones their
 * workflow needs; anything not listed renders no control and stays inert.
 *
 * - `navigation`: previous/next buttons flanking the image.
 * - `zoom`: zoom in/out controls in the footer bar.
 * - `pan`: pointer drag moves the image once it is zoomed past its fit size.
 * - `download`: download action in the header bar.
 */
export type ImagePreviewTool = 'navigation' | 'zoom' | 'pan' | 'download';
</script>

<script setup lang="ts">
import { computed, useSlots, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import PreviewDialog from '@satellite/components/elements/preview/PreviewDialog.vue';
import { resolvePreviewSource } from '@satellite/components/elements/preview/previewSource';
import { useImagePreviewNavigation } from '@satellite/composables/useImagePreviewNavigation';
import { useImageZoomPan } from '@satellite/composables/useImageZoomPan';
import { downloadFile } from '@satellite/helpers/downloadFile';

import type { PreviewDialogSize } from '@satellite/components/elements/preview/PreviewDialog.vue';
import type { PreviewSource } from '@satellite/components/elements/preview/previewSource';

import type { Maybe } from '@nova/core';

type ImageStatus = 'loading' | 'loaded' | 'error';

defineOptions({ name: 'ImagePreviewDialog' });

const {
  modelValue,
  images,
  index,
  alt,
  fileName = '',
  tools = [],
  isImageClickable = false,
  imageAriaLabel = '',
  imageClass,
  downloadSource,
  maxWidth = '1080px',
  closeOnScrim = true,
  size = 'adaptive',
  scrimOpacity = null,
  testId = 'image-preview'
} = defineProps<{
  /**
   * Whether the preview is open.
   */
  modelValue: boolean;
  /**
   * Image sources to preview. A single-item array renders a plain preview.
   */
  images: string[];
  /**
   * Optional controlled index. Leave unset to let the preview own it.
   */
  index?: number;
  /**
   * Alternative text for the image. Left unset the image is marked decorative, so
   * pass one wherever the photo itself carries meaning.
   */
  alt?: string;
  /**
   * Shown in the header bar and used as the downloaded file name.
   */
  fileName?: string;
  /**
   * Which controls this preview offers. Defaults to none, i.e. a plain preview.
   */
  tools?: ImagePreviewTool[];
  /**
   * Makes the image itself activatable (pointer and keyboard), reported through
   * `click:image` — for consumers that attach their own behavior to it.
   */
  isImageClickable?: boolean;
  imageAriaLabel?: string;
  imageClass?: string;
  /**
   * Where the `download` tool reads bytes from. Defaults to the image on screen, which
   * a browser can only save when its origin allows the fetch; point this at an API when
   * it does not.
   */
  downloadSource?: PreviewSource;
  maxWidth?: string | number;
  closeOnScrim?: boolean;
  /**
   * How the dialog takes its size. `fullscreen` holds the bars at the viewport edges, so
   * stepping between images of different shapes no longer moves them.
   */
  size?: PreviewDialogSize;
  /**
   * How far the scrim dims the page behind it, 0 to 1. Defaults to Vuetify's own.
   */
  scrimOpacity?: Maybe<number | string>;
  /**
   * Prefix for every `data-testid` this preview renders, e.g. `<testId>-image`.
   */
  testId?: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'update:index': [value: number];
  close: [];
  load: [src: string];
  error: [src: string];
  'error:download': [error: unknown];
  'click:image': [src: string];
}>();

const { t } = useI18n();
const slots = useSlots();

const imageStatus = ref<ImageStatus>('loading');

const imagesRef = computed(() => images);
const isNavigationEnabled = computed(() => tools.includes('navigation'));
const isZoomEnabled = computed(() => tools.includes('zoom'));
const isPanEnabled = computed(() => tools.includes('pan'));
const isDownloadEnabled = computed(() => tools.includes('download'));

const {
  currentIndex,
  currentImage,
  total,
  hasPrevious,
  hasNext,
  goToIndex,
  goToPrevious,
  goToNext
} = useImagePreviewNavigation({ images: imagesRef });

const {
  viewportRef,
  imageRef,
  zoom,
  canZoomIn,
  canZoomOut,
  imageStyle,
  zoomIn,
  zoomOut,
  reset: resetZoomPan,
  onPanStart,
  onPanMove,
  onPanEnd
} = useImageZoomPan({ isZoomEnabled, isPanEnabled });

const isOpen = computed({
  get: () => modelValue,
  set: value => emit('update:modelValue', value)
});

// No fallback to the file name: the header bar already renders it, so repeating it
// here only has a screen reader announce the same string twice. An empty alt marks
// the image decorative, which is the quiet option; dropping the attribute would have
// assistive tech read out the source URL instead.
const imageAlt = computed(() => alt ?? '');

// The footer bar only exists for the zoom controls, so a plain preview has none.
const hasFooter = computed(() => Boolean(slots.footer) || isZoomEnabled.value);

function close() {
  isOpen.value = false;
  emit('close');
}

async function download() {
  // Held outside the try so an object URL is released whether or not saving it worked.
  let ownedUrl: string | null = null;

  try {
    const { url, isOwned } = await resolvePreviewSource(downloadSource ?? currentImage.value ?? '');
    ownedUrl = isOwned ? url : null;

    await downloadFile(url, fileName);
  } catch (error) {
    // A rejecting `downloadSource` is the consumer's to report; nothing awaits this
    // handler, so letting it through would only be an unhandled rejection.
    emit('error:download', error);
  } finally {
    if (ownedUrl) {
      URL.revokeObjectURL(ownedUrl);
    }
  }
}

function emitImageClick() {
  // The image is always interactive for panning, so activation only reports a
  // click when the consumer asked for one.
  if (isImageClickable && currentImage.value) {
    emit('click:image', currentImage.value);
  }
}

function onImageLoad() {
  imageStatus.value = 'loaded';

  if (currentImage.value) {
    emit('load', currentImage.value);
  }
}

function onImageError() {
  imageStatus.value = 'error';

  if (currentImage.value) {
    emit('error', currentImage.value);
  }
}

const slotScope = computed(() => ({
  images,
  currentImage: currentImage.value,
  currentIndex: currentIndex.value,
  total: total.value,
  hasPrevious: hasPrevious.value,
  hasNext: hasNext.value,
  isLoading: imageStatus.value === 'loading',
  hasError: imageStatus.value === 'error',
  zoom: zoom.value,
  canZoomIn: canZoomIn.value,
  canZoomOut: canZoomOut.value,
  goToIndex,
  goToPrevious,
  goToNext,
  zoomIn,
  zoomOut,
  download,
  close
}));

watch(
  () => index,
  value => {
    if (typeof value === 'number' && value !== currentIndex.value) {
      goToIndex(value);
    }
  },
  { immediate: true }
);

watch(currentIndex, value => emit('update:index', value));

// A new image starts from its fit size, and is not "loaded" until it says so.
watch(
  currentImage,
  () => {
    imageStatus.value = 'loading';
    resetZoomPan();
  },
  { immediate: true }
);

watch(isOpen, value => {
  if (!value) {
    return;
  }

  goToIndex(index ?? 0);
  resetZoomPan();
});
</script>

<template>
  <preview-dialog
    v-model="isOpen"
    :file-name="fileName"
    :max-width="maxWidth"
    :close-on-scrim="closeOnScrim"
    :size="size"
    :scrim-opacity="scrimOpacity"
    :show-download="isDownloadEnabled"
    :is-loading="imageStatus === 'loading' && Boolean(currentImage)"
    :test-id="testId"
    @close="emit('close')"
    @download="download"
    @previous="goToPrevious"
    @next="goToNext">
    <!-- @slot Replaces the header bar contents. -->
    <template v-if="slots.header" #header>
      <slot name="header" v-bind="slotScope" />
    </template>

    <!-- @slot Extra actions, rendered before the built-in ones. -->
    <template v-if="slots['header-actions']" #header-actions>
      <slot name="header-actions" v-bind="slotScope" />
    </template>

    <div class="image-preview__stage">
      <!-- @slot Replaces the previous button. -->
      <slot name="nav-previous" v-bind="slotScope">
        <!-- Both buttons stay mounted and disable at the ends of the set, so
               stepping through it never shifts the image sideways. -->
        <v-btn
          v-if="isNavigationEnabled"
          icon="mdi-chevron-left"
          variant="elevated"
          :disabled="!hasPrevious"
          :aria-label="t('global.basicTerms.previous')"
          :data-testid="`${testId}-previous`"
          @click="goToPrevious" />
      </slot>

      <!-- The image and whatever sits under it are one unit sized to the image,
             so anything slotted below can never run wider than the photo. -->
      <div class="image-preview__figure">
        <div ref="viewportRef" class="image-preview__viewport">
          <v-progress-circular
            v-if="imageStatus === 'loading' && currentImage"
            class="image-preview__status"
            indeterminate
            color="primary"
            :data-testid="`${testId}-loader`" />

          <!-- @slot Replaces the broken-image placeholder. -->
          <slot v-if="imageStatus === 'error'" name="error" v-bind="slotScope">
            <div class="image-preview__status" :data-testid="`${testId}-error`">
              <v-icon icon="mdi-image-broken-variant" size="48" />
            </div>
          </slot>

          <img
            v-if="currentImage"
            v-show="imageStatus !== 'error'"
            ref="imageRef"
            :key="currentImage"
            :src="currentImage"
            :alt="imageAlt"
            :style="imageStyle"
            :class="[imageClass, { 'image-preview__image--pannable': isPanEnabled }]"
            :role="isImageClickable ? 'button' : undefined"
            :tabindex="isImageClickable ? 0 : undefined"
            :aria-label="isImageClickable ? imageAriaLabel : undefined"
            class="image-preview__image"
            draggable="false"
            :data-testid="`${testId}-image`"
            @load="onImageLoad"
            @error="onImageError"
            @click="emitImageClick"
            @keydown.enter="emitImageClick"
            @keydown.space.prevent="emitImageClick"
            @pointerdown="onPanStart"
            @pointermove="onPanMove"
            @pointerup="onPanEnd"
            @pointercancel="onPanEnd" />
        </div>

        <!-- @slot Sits directly under the image, matching its width. -->
        <slot name="image-footer" v-bind="slotScope" />
      </div>

      <!-- @slot Replaces the next button. -->
      <slot name="nav-next" v-bind="slotScope">
        <v-btn
          v-if="isNavigationEnabled"
          icon="mdi-chevron-right"
          variant="elevated"
          :disabled="!hasNext"
          :aria-label="t('global.basicTerms.next')"
          :data-testid="`${testId}-next`"
          @click="goToNext" />
      </slot>
    </div>

    <!-- @slot Replaces the footer bar contents. -->
    <template v-if="hasFooter" #footer>
      <slot name="footer" v-bind="slotScope">
        <v-btn
          icon="mdi-magnify-minus-outline"
          variant="text"
          density="comfortable"
          :disabled="!canZoomOut"
          :aria-label="t('global.basicTerms.zoomOut')"
          :data-testid="`${testId}-zoom-out`"
          @click="zoomOut" />
        <v-btn
          icon="mdi-magnify-plus-outline"
          variant="text"
          density="comfortable"
          :disabled="!canZoomIn"
          :aria-label="t('global.basicTerms.zoomIn')"
          :data-testid="`${testId}-zoom-in`"
          @click="zoomIn" />
      </slot>
    </template>
  </preview-dialog>
</template>

<style lang="scss" scoped>
.image-preview__stage {
  position: relative;
  display: flex;
  // Takes whatever height the bars leave, and no more.
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 0;
}

.image-preview__figure {
  display: flex;
  // Full height, so the image below has a size to fit into; its contents stay
  // centered, keeping anything slotted under the image right below it.
  align-self: stretch;
  flex-direction: column;
  justify-content: center;
  // Shrink to the image so anything slotted below it is exactly the image width.
  width: fit-content;
  min-width: 0;
  min-height: 0;
}

.image-preview__viewport {
  position: relative;
  display: flex;
  flex: 0 1 auto;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  min-height: 0;
  overflow: hidden;
}

.image-preview__image {
  display: block;
  max-width: 100%;
  // Fits the space left over, rather than a share of the screen: a viewport-based
  // cap ignores the bars and paints the image over them on short screens.
  max-height: 100%;
  object-fit: contain;
}

.image-preview__image--pannable {
  user-select: none;
  // Let pointer drags pan the image instead of scrolling/gesturing the page.
  touch-action: none;
}

.image-preview__status {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
</style>
